import type { BankTransactionDto } from "Infrastructure/Api/Api";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { useDateFormat } from "Infrastructure/Utilities/dateFormat";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { OrderPaths } from "Order/Routes/Routing";
import { usePaymentsQuery } from "Payment/Api/Query/usePaymentsQuery";
import { unassignedPaymentsAtom } from "Payment/Atom/paymentAtom";
import { usePaymentFilters } from "Payment/Hooks/usePaymentFilters";
import { Chip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { createMRTColumnHelper } from "material-react-table";
import { useEffect, useMemo } from "react";
import { Link } from "react-router";

type BankTransactionTableRow = BankTransactionDto;

export const usePaymentsTable = () => {
	const columnHelper = createMRTColumnHelper<BankTransactionTableRow>();
	const filters = usePaymentFilters();
	const query = usePaymentsQuery(filters);
	const { formatDate } = useDateFormat();
	const [_, setUnassignedPaymentsNum] = useAtom(unassignedPaymentsAtom);

	const tableData = useMemo(() => query.data?.data ?? [], [query.data?.data]);

	// Filter out only "unassigned" payments (those without orderId)
	const filteredTableData = useMemo(() => {
		return tableData.filter((payment) => !payment.orderId);
	}, [tableData]);

	useEffect(() => {
		setUnassignedPaymentsNum(filteredTableData.length);
	}, [filteredTableData, setUnassignedPaymentsNum]);

	const columns = [
		columnHelper.accessor((row) => row.createdAt, {
			id: "createdAt",
			header: "Date Created",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.createdAt)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.amount, {
			id: "amount",
			header: "Amount",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatCurrency(
						row.original.currency,
						row.original.currency,
						row.original.amount,
					)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.reference, {
			id: "reference",
			header: "Reference",
			enableColumnActions: false,
			maxSize: 150,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.reference}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.updatedAt, {
			id: "updatedAt",
			header: "Latest sync",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{formatDate(row.original.updatedAt)}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.bankDetails.bankAccount, {
			id: "bankAccount",
			header: "Bank Account",
			enableColumnActions: false,
			maxSize: 120,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">
					{row.original.bankDetails.iban.trim().length > 0
						? row.original.bankDetails.iban
						: row.original.bankDetails.bankAccount}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.note, {
			id: "note",
			header: "Note",
			enableColumnActions: false,
			maxSize: 250,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) => (
				<Typography variant="tableCell">{row.original.note}</Typography>
			),
		}),
		columnHelper.accessor((row) => row.orderId, {
			id: "orderId",
			header: "Order ID",
			enableColumnActions: false,
			maxSize: 80,
			minSize: 0,
			grow: 0,
			Cell: ({ row }) =>
				row.original.orderId != null && (
					<Link to={OrderPaths.Payment.path(row.original.orderId)}>
						<Typography color="secondary" variant="tableCell">
							{row.original.orderId}
						</Typography>
					</Link>
				),
		}),
		columnHelper.accessor((row) => !!row.orderId, {
			id: "matched",
			header: "Matched",
			enableColumnActions: false,
			maxSize: 80,
			minSize: 0,
			grow: 0,
			Cell: ({ cell }) => {
				const isMatched = cell.getValue();

				return (
					<Chip
						label={isMatched ? "✅ Matched" : "❌ Unmatched"}
						color={isMatched ? "success" : "error"}
						size="small"
					/>
				);
			},
		}),
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: query.isLoading,
			isLoading: query.isLoading,
		},
	};

	return tableOptions;
};
